/**
 * The shared-ride page's own logic (owner, 2026-11-04): the page a person opens from a shared link, with no app. It shows the
 * ride on Vello's own basemap (the api's self-hosted style, the same tiles the apps draw) instead of sending the visitor to a
 * third-party map.
 *
 * Pure: what the page is made of (a plain state, or the map with a sheet), what the map must draw, when to stop polling, and the
 * arithmetic that moves the car smoothly between two reads. `web/TripShareView.tsx` renders it; `web/trip-map-engine.ts` draws it.
 * Neutral: no country, language, unit or vendor is named here.
 *
 * The scene carries what the public read returns: the car (with its heading when sent), the destination point and the road ahead
 * of the car. All but the car are optional: a link without them, or a road the server could not get, draws the car alone.
 */
import { renderCssVariables, WEB_DARK_THEME } from '@vello/tokens';
import type { TripSharePageView } from './public-pages.js';

export interface MapPoint {
  readonly latitude: number;
  readonly longitude: number;
}

/** What the map draws. Every member but `car` is optional (sent by the server only when the link allows it). */
export interface TripMapScene {
  readonly car?: { readonly position: MapPoint; readonly updatedAt?: string; readonly headingDegrees?: number };
  readonly destination?: MapPoint;
  /** The road ahead of the car, drawn as the server sent it (the page never asks a router). */
  readonly route?: readonly MapPoint[];
}

/** What the page shows. `plain` is the state text on a plain ground (no WebGL, or no position yet). */
export type TripPageModel =
  | { readonly mode: 'loading' }
  | { readonly mode: 'ended'; readonly messageId: string }
  | { readonly mode: 'unavailable' }
  | {
      readonly mode: 'map' | 'plain';
      readonly stateMessageId: string;
      readonly etaMinutes?: number;
      /** Optional rows of the sheet: shown when the server sends them and the owner allows them. */
      readonly driverName?: string;
      readonly vehicle?: string;
      readonly updatedAt?: string;
      readonly scene: TripMapScene;
      /** `plain` only: the device cannot draw the map although a position is known. */
      readonly mapUnavailable: boolean;
    };

/** The sheet's and the map's decision for one read. `canDrawMap` is the device's WebGL answer. */
export function tripPageModel(view: TripSharePageView | undefined, canDrawMap: boolean): TripPageModel {
  if (view === undefined) return { mode: 'loading' };
  if (view.kind === 'ended') return { mode: 'ended', messageId: view.messageId };
  if (view.kind === 'unavailable') return { mode: 'unavailable' };
  const scene: TripMapScene = view.position !== undefined
    ? {
        car: {
          position: { latitude: view.position.latitude, longitude: view.position.longitude },
          ...(view.position.updatedAt !== undefined ? { updatedAt: view.position.updatedAt } : {}),
          ...(view.position.headingDegrees !== undefined ? { headingDegrees: view.position.headingDegrees } : {}),
        },
        // The road and the destination only make sense with the car on the map; without them the map is the car alone.
        ...(view.destination !== undefined ? { destination: view.destination } : {}),
        ...(view.route !== undefined ? { route: view.route } : {}),
      }
    : {};
  return {
    mode: canDrawMap && scene.car !== undefined ? 'map' : 'plain',
    stateMessageId: view.stateMessageId,
    ...(view.etaMinutes !== undefined ? { etaMinutes: view.etaMinutes } : {}),
    ...(view.driverName !== undefined ? { driverName: view.driverName } : {}),
    ...(view.vehicle !== undefined ? { vehicle: view.vehicle } : {}),
    ...(scene.car?.updatedAt !== undefined ? { updatedAt: scene.car.updatedAt } : {}),
    scene,
    mapUnavailable: !canDrawMap && scene.car !== undefined,
  };
}

/** An ended, revoked or expired link is final: it is never read again. A live or unreachable one is. */
export function keepsPolling(view: TripSharePageView | undefined): boolean {
  return view?.kind !== 'ended';
}

// ---- The car on the map ---------------------------------------------------------------------------------------------

const EARTH_RADIUS_METERS = 6_371_008.8;
const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in metres. */
export function distanceMeters(a: MapPoint, b: MapPoint): number {
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial compass bearing from `a` to `b`, in degrees [0, 360). */
export function bearingDegrees(a: MapPoint, b: MapPoint): number {
  const dLon = rad(b.longitude - a.longitude);
  const y = Math.sin(dLon) * Math.cos(rad(b.latitude));
  const x = Math.cos(rad(a.latitude)) * Math.sin(rad(b.latitude)) - Math.sin(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.cos(dLon);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

/** The signed shortest turn from `from` to `to`, in degrees (-180, 180]. */
export function shortestTurn(from: number, to: number): number {
  const d = (((to - from) % 360) + 540) % 360 - 180;
  return d === -180 ? 180 : d;
}

/** Linear step between two reads (they are seconds apart, so a straight line is exact enough). `t` is clamped to [0, 1]. */
export function lerpPoint(from: MapPoint, to: MapPoint, t: number): MapPoint {
  const k = Math.min(1, Math.max(0, t));
  return { latitude: from.latitude + (to.latitude - from.latitude) * k, longitude: from.longitude + (to.longitude - from.longitude) * k };
}

/** Motion tuning of the car. Nothing here is a business rule; it is how the page draws a point that arrives every few seconds. */
export const CAR_MOTION = {
  /** The car glides over this long, a little less than a poll, so it arrives before the next read. */
  glideMs: 9_000,
  /** A jump longer than this (a gap in reads, a new leg) is not driven: the car appears at the new place. */
  teleportAboveMeters: 2_500,
  /** A move shorter than this is GPS noise and does not turn the car. */
  turnAboveMeters: 4,
  /** The first view of the car, in MapLibre zoom (512 px tiles). */
  startZoom: 15.5,
} as const;

/** How the car moves to a new read: where it starts, whether it glides, and which way it points when it gets there. */
export function carMove(from: { readonly position: MapPoint; readonly headingDegrees: number } | undefined, to: { readonly position: MapPoint; readonly headingDegrees?: number }): {
  readonly glide: boolean;
  readonly headingDegrees: number;
} {
  const base = from?.headingDegrees ?? 0;
  if (to.headingDegrees !== undefined && Number.isFinite(to.headingDegrees)) return { glide: from !== undefined && distanceMeters(from.position, to.position) <= CAR_MOTION.teleportAboveMeters, headingDegrees: ((to.headingDegrees % 360) + 360) % 360 };
  if (from === undefined) return { glide: false, headingDegrees: base };
  const metres = distanceMeters(from.position, to.position);
  if (metres > CAR_MOTION.teleportAboveMeters) return { glide: false, headingDegrees: metres > CAR_MOTION.turnAboveMeters ? bearingDegrees(from.position, to.position) : base };
  return { glide: true, headingDegrees: metres >= CAR_MOTION.turnAboveMeters ? bearingDegrees(from.position, to.position) : base };
}

/**
 * Keep the car in view: true when its screen point is outside the safe box (the map minus an edge margin, minus the sheet at the
 * bottom), so the camera must follow. Inside the box the camera stays still and the car moves across the map.
 */
/**
 * Everything the camera keeps in view: the car, the road ahead and the destination, as [west, south, east, north]. D939: the destination must be on screen, or the person with the link
 * cannot see where the car is going. Undefined without a car: a road or a destination never shows alone.
 */
export function sceneBounds(scene: TripMapScene): readonly [number, number, number, number] | undefined {
  if (scene.car === undefined) return undefined;
  const points = [scene.car.position, ...(scene.route ?? []), ...(scene.destination !== undefined ? [scene.destination] : [])];
  const lats = points.map((p) => p.latitude);
  const lngs = points.map((p) => p.longitude);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

export function needsRecenter(point: { readonly x: number; readonly y: number }, size: { readonly width: number; readonly height: number }, insets: { readonly top: number; readonly bottom: number; readonly side: number }): boolean {
  return point.x < insets.side || point.x > size.width - insets.side || point.y < insets.top || point.y > size.height - insets.bottom;
}

/**
 * D943: the frame holds the car AND the destination. True when any of the given screen points (the car, then the destination when
 * the trip has one) is outside the safe box, so the camera refits; while all stay inside it the camera is still. A refit lands
 * both points inside the box, so it cannot repeat on the next read.
 */
export function needsReframe(points: readonly { readonly x: number; readonly y: number }[], size: { readonly width: number; readonly height: number }, insets: { readonly top: number; readonly bottom: number; readonly side: number }): boolean {
  return points.some((p) => needsRecenter(p, size, insets));
}

// ---- Theme ------------------------------------------------------------------------------------------------------------

/**
 * The page is light by default (the site's tokens) and takes the dark theme of the apps under `prefers-color-scheme: dark`:
 * the token set of `WEB_DARK_THEME`, scoped to `.trip-page` so the institutional site and the legal texts are untouched.
 */
export function tripPageThemeCss(): string {
  return `@media (prefers-color-scheme: dark) {\n  .trip-page {\n    color-scheme: dark;\n${renderCssVariables(WEB_DARK_THEME)}\n  }\n}\n`;
}
